import { ChangeEventHandler, useState } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { SpotToast } from "../SpotToast";
import { FileDropTarget } from "@/components/Form/FileDropTarget";
import { SelectedFileRow } from "@/components/Form/SelectedFileRow";
import {
  FileTypes,
  areValidFileTypes,
  getAcceptAttributeValue,
  getMaxFileSize,
} from "@/utils/mimetypes";

interface IFileBase64Upload {
  setValue: (base64: string) => void;
  value?: string;
  allowed_file_types?: FileTypes[];
  isDisabled?: boolean;
  fileName?: string;
  setFileName?: (name?: string) => void;
}

const FileBase64Upload = ({
  value,
  setValue,
  allowed_file_types,
  isDisabled,
  fileName,
  setFileName,
}: IFileBase64Upload) => {
  const [loading, setLoading] = useState(false);
  const MAX_FILE_SIZE = getMaxFileSize();

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = async (
    event
  ) => {
    processFile(event.target.files);
  };

  const handleFileInputClick = () => {
    const tempInput = document.createElement("input");
    tempInput.type = "file";
    tempInput.multiple = false;
    tempInput.accept = getAcceptAttributeValue(allowed_file_types);
    tempInput.style.display = "none";

    tempInput.onchange = (event) => {
      handleOnChange(event as any);
      document.body.removeChild(tempInput);
    };

    document.body.appendChild(tempInput);
    tempInput.click();
  };

  const processFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (!areValidFileTypes(files, allowed_file_types)) {
      SpotToast.error("Invalid file type");
      return;
    }

    if (files.length !== 1) {
      SpotToast.error("You can only upload one file.");
      return;
    }

    const file = files[0];

    if (file.size > MAX_FILE_SIZE) {
      SpotToast.error(
        `File size exceeds the ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`
      );
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64 = result.split(",")[1];
        setValue(base64);
        setFileName?.(file.name);
        setLoading(false);
      };
      reader.onerror = () => {
        SpotToast.error("Failed to read file");
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      SpotToast.error("Failed to process file");
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <FileDropTarget
        onClick={handleFileInputClick}
        onFiles={processFile}
        loading={loading}
        isDisabled={isDisabled}
        allowedFileTypes={allowed_file_types}
      />
      {fileName && (
        <SelectedFileRow
          name={fileName}
          isDisabled={isDisabled}
          onRemove={() => {
            setValue("");
            setFileName?.(undefined);
          }}
        />
      )}
    </div>
  );
};

interface IFileBase64Field {
  name: string;
  allowed_file_types?: FileTypes[];
  control: Control<any>;
  label?: string;
  isDisabled?: boolean;
  rules?: RegisterOptions;
  tooltip?: string;
  defaultValue?: string;
}

export const FileBase64Field = ({
  name,
  allowed_file_types,
  control,
  isDisabled,
  label,
  rules,
  tooltip,
  defaultValue,
}: IFileBase64Field) => {
  const [fileName, setFileName] = useState<string>();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }) => {
        return (
          <div className="w-full">
            {label != undefined && (
              <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
            )}
            <FileBase64Upload
              value={field.value}
              setValue={field.onChange}
              isDisabled={isDisabled}
              allowed_file_types={allowed_file_types}
              fileName={fileName}
              setFileName={setFileName}
            />
          </div>
        );
      }}
    />
  );
};
